import React, { useEffect } from "react";
import {
  Trash2,
  Mail,
  FileCheck,
  ShieldAlert,
  Clock3,
  User,
  Phone,
  Database,
  History,
  CheckCircle2,
} from "lucide-react";
import HomeHeader2 from "@/components/homePage/HomeHeader2";
import HomeFooter2 from "@/components/homePage/HomeFooter2";

const COLORS = {
  orange: "#E87E04",
  white: "#FFFFFF",
  green: "#008751",
  darkGreen: "#006B3F",
  lightOrange: "#FFE0B2",
  lightGreen: "#CCFFCC",
  gray: "#718096",
  lightGray: "#E2E8F0",
  paleGreen: "#E6FFEA",
};

const deletedData = [
  { label: "Nom", icon: <User className="w-5 h-5" /> },
  { label: "Email", icon: <Mail className="w-5 h-5" /> },
  { label: "Numero de telephone", icon: <Phone className="w-5 h-5" /> },
  { label: "Donnees de profil", icon: <Database className="w-5 h-5" /> },
  { label: "Historique d'utilisation", icon: <History className="w-5 h-5" /> },
];

export default function SuppressionComptePage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <HomeHeader2 />

      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-green-50">
        <section
          className="relative overflow-hidden py-16 px-4"
          style={{
            background: `linear-gradient(135deg, ${COLORS.orange} 0%, ${COLORS.green} 100%)`,
          }}
        >
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-10 left-10 w-36 h-36 rounded-full bg-white" />
            <div className="absolute top-24 right-16 w-20 h-20 rounded-full bg-white" />
            <div className="absolute bottom-8 left-1/3 w-24 h-24 rounded-full bg-white" />
          </div>

          <div className="max-w-5xl mx-auto text-center relative z-10">
            <Trash2 className="w-16 h-16 text-white mx-auto mb-5" />
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Demande de suppression de compte
            </h1>
            <p className="text-lg md:text-xl text-white/90 max-w-3xl mx-auto leading-relaxed">
              Les utilisateurs de l'application Kssarmou peuvent demander la suppression de leur compte
              ainsi que de leurs donnees personnelles.
            </p>
          </div>
        </section>

        <section className="max-w-5xl mx-auto px-4 py-12">
          <div
            className="rounded-2xl p-7 md:p-8 shadow-lg border-l-4 mb-8"
            style={{ backgroundColor: COLORS.paleGreen, borderLeftColor: COLORS.green }}
          >
            <div className="flex items-start gap-4">
              <Mail className="w-7 h-7 mt-1" style={{ color: COLORS.darkGreen }} />
              <div>
                <h2 className="text-2xl font-bold mb-3" style={{ color: COLORS.darkGreen }}>
                  Pour demander la suppression
                </h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Envoyez un email avec l'objet <strong>Suppression de compte</strong> a l'adresse suivante.
                </p>
                <a
                  href="mailto:support@kassarmoumarket.com?subject=Suppression%20de%20compte"
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-lg text-white font-semibold shadow-md hover:scale-[1.02] transition-transform"
                  style={{ backgroundColor: COLORS.green }}
                >
                  <Mail className="w-5 h-5" />
                  support@kassarmoumarket.com
                </a>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-2xl p-6 shadow-md border border-gray-100">
              <div className="flex items-center gap-3 mb-4">
                <FileCheck className="w-6 h-6" style={{ color: COLORS.orange }} />
                <h3 className="text-xl font-bold text-gray-800">Donnees supprimees</h3>
              </div>

              <ul className="space-y-3">
                {deletedData.map((item) => (
                  <li
                    key={item.label}
                    className="flex items-center gap-3 p-3 rounded-lg"
                    style={{ backgroundColor: COLORS.lightOrange }}
                  >
                    <span style={{ color: COLORS.orange }}>{item.icon}</span>
                    <span className="text-gray-800 font-medium">{item.label}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="space-y-6">
              <div className="bg-white rounded-2xl p-6 shadow-md border border-gray-100">
                <div className="flex items-center gap-3 mb-3">
                  <Clock3 className="w-6 h-6" style={{ color: COLORS.green }} />
                  <h3 className="text-xl font-bold text-gray-800">Delai de suppression</h3>
                </div>
                <p className="text-gray-700 leading-relaxed">
                  Les donnees sont supprimees sous <strong>7 jours</strong> apres verification de la demande.
                </p>
              </div>

              <div
                className="rounded-2xl p-6 border"
                style={{ backgroundColor: "#FFF7ED", borderColor: "#FDBA74" }}
              >
                <div className="flex items-start gap-3">
                  <ShieldAlert className="w-6 h-6 mt-0.5" style={{ color: COLORS.orange }} />
                  <div>
                    <h3 className="text-lg font-bold text-gray-800 mb-2">Conservation exceptionnelle</h3>
                    <p className="text-gray-700 leading-relaxed">
                      Certaines donnees peuvent etre conservees temporairement pour des raisons legales,
                      de securite ou de prevention de fraude.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div
            className="rounded-2xl p-7 text-center"
            style={{ background: `linear-gradient(135deg, ${COLORS.lightGreen} 0%, ${COLORS.lightOrange} 100%)` }}
          >
            <CheckCircle2 className="w-10 h-10 mx-auto mb-3" style={{ color: COLORS.darkGreen }} />
            <h3 className="text-2xl font-bold mb-3" style={{ color: COLORS.darkGreen }}>
              Traitement transparent et securise
            </h3>
            <p className="text-gray-700 max-w-2xl mx-auto leading-relaxed">
              Chaque demande est traitee avec attention, dans le respect de votre vie privee et des obligations
              reglementaires applicables.
            </p>
          </div>
        </section>
      </div>

      <HomeFooter2 />
    </>
  );
}
